/**
 * A TENSOFT-KAPCSOLAT KÁRTYÁJA — a NAV és Tensoft fül alja (#54, 2. lépés).
 *
 * Viktor döntése artboardon (2026-10-05, `docs/tervezes/design/tensoft-token/`):
 * az „A" forma. A frissen kiadott token A KÁRTYÁN jelenik meg, nem ablakban,
 * és addig marad, amíg a könyvelő nem jelzi, hogy elmentette.
 *
 * ═══ AZ `Escape` NEM VIHETI EL A TOKENT ═══
 *
 * Ez volt a döntés oka. A token szövege EGYSZER jön (`useAgentTokens`), és egy
 * reflexből nyomott `Escape` egy ablakkal együtt örökre elvinné. Itt a lap
 * `Escape`-je a Beállításokat zárná — ugyanazzal az eredménnyel. Ezért amíg a
 * doboz látszik, a szelet egy NYELŐ réteget jegyez be (`useSettingsLayer`,
 * `saving` ág): a leütés nem zár semmit. A doboz csak a gombra tűnik el.
 *
 * ⚠️ AMIT NEM TUDUNK MEGAKADÁLYOZNI: egy másik fülre kattintást. A doboz ezt
 * kimondja; a kiadás gombja addig nem nyomható, hogy egy második kiadás se
 * írja felül az elsőt.
 */

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '@/features/settings/ConfirmDialog';
import { type LayerRegistrar, useSettingsLayer } from '@/features/settings/useSettingsLayer';
import { formatDateTime } from '@/i18n/generated';
import { type AgentToken, type AgentTokensState, useAgentTokens } from './useAgentTokens';

/** A szerver `IssueAgentTokenDto`-ja: 3–120 karakter. A gomb ennél előbb nem él. */
const LABEL_MIN = 3;
const LABEL_MAX = 120;

const noop = () => undefined;

/**
 * A FELTÖLTŐ CÍME — a backend saját hosztja, NEM az adminé.
 *
 * ⚠️ A feltöltés nem mehet az admin nginx-én át: ott a törzs felső határa 1 MiB
 * (`tensoft-feltoltes-szerzodes.md` §2). A hosztnevek a starterkit mintáját
 * követik (`admin.<alap>` ↔ `backend.<alap>`); ahol nem így áll, `null` — és a
 * parancsban egy kitöltendő hely marad, nem egy rossz cím.
 */
export function uploaderBackendUrl(
  location: Pick<Location, 'protocol' | 'hostname'>
): string | null {
  const prefix = 'admin.';
  if (!location.hostname.startsWith(prefix)) {
    return null;
  }
  return `${location.protocol}//backend.${location.hostname.slice(prefix.length)}`;
}

/** Előbb az érvényesek (újabb elöl), a visszavontak a lista alján. */
export function orderTokens(tokens: AgentToken[]): AgentToken[] {
  const active = tokens.filter((token) => token.revokedAt === null);
  const revoked = tokens.filter((token) => token.revokedAt !== null);
  return [...active, ...revoked];
}

/** A legutóbbi jelentkezés az ÉRVÉNYES tokenek közül — a kártya fejlécének jelzése. */
export function lastContact(tokens: AgentToken[]): string | null {
  return tokens
    .filter((token) => token.revokedAt === null && token.lastUsedAt !== null)
    .map((token) => token.lastUsedAt as string)
    .reduce<string | null>((latest, at) => (latest === null || at > latest ? at : latest), null);
}

export function TensoftConnection({ onLayer }: { onLayer: LayerRegistrar }) {
  const { t } = useTranslation();
  const state = useAgentTokens();
  const [confirming, setConfirming] = useState<AgentToken | null>(null);

  useSettingsLayer(onLayer, {
    hidden: state.status !== 'ready',
    confirming: confirming !== null,
    // A KIADOTT TOKEN DOBOZA „mentés alatti űrlapként" jelentkezik be: a réteg
    // NYELI az `Escape`-et, de nem zár (lásd a fejlécet).
    // ⚠️ A KÉRÉS ALATT IS (`busy`, code review 10-05): a kiadás válasza még úton
    // van, az `issued` még `null` — egy ekkor nyomott `Escape` a lapot zárná, a
    // válasz elveszne, a token pedig érvényesen, de senki által nem látva
    // maradna a szerveren. Ugyanaz, mint a `UserSettings` `saving` ága.
    editing: state.issued !== null || state.busy,
    saving: state.issued !== null || state.busy,
    closeConfirm: () => setConfirming(null),
    closeForm: noop,
  });

  return (
    <div className="card navSettings__tensoft tensoft">
      <div className="tensoft__head">
        <div>
          <h3>{t('navSettings.tensoftTitle')}</h3>
          <p className="subtitle">{t('tensoft.lead')}</p>
        </div>
        {state.status === 'ready' ? <ContactBadge tokens={state.tokens} /> : null}
      </div>

      <TensoftBody state={state} onRevoke={setConfirming} />

      {confirming ? (
        /* A szöveg a KÖVETKEZMÉNYT mondja: a gép ezután nem tölt fel, és a
           kontír nem frissül. Ez a gombon nem látszik. */
        <ConfirmDialog
          message={t('tensoft.revokeConfirm', { label: confirming.label })}
          yesLabel={t('tensoft.revokeYes')}
          noLabel={t('tensoft.revokeNo')}
          onYes={() => {
            state.revoke(confirming.id);
            setConfirming(null);
          }}
          onNo={() => setConfirming(null)}
        />
      ) : null}
    </div>
  );
}

function TensoftBody({
  state,
  onRevoke,
}: {
  state: AgentTokensState;
  onRevoke: (token: AgentToken) => void;
}) {
  const { t } = useTranslation();

  if (state.status === 'loading') {
    return <p className="subtitle">{t('tensoft.loading')}</p>;
  }
  if (state.status === 'forbidden' || state.status === 'listFailed') {
    return (
      <output className="notice">
        {state.status === 'forbidden' ? t('tensoft.forbidden') : t('tensoft.listFailed')}
      </output>
    );
  }

  return (
    <>
      {state.tokens.length === 0 ? (
        <p className="empty">{t('tensoft.noTokens')}</p>
      ) : (
        <TokenTable
          tokens={state.tokens}
          freshId={state.issued?.id ?? null}
          busy={state.busy}
          onRevoke={onRevoke}
        />
      )}
      {state.issued ? <IssuedToken state={state} /> : null}
      <WriteError state={state} />
      <IssueForm state={state} />
    </>
  );
}

function ContactBadge({ tokens }: { tokens: AgentToken[] }) {
  const { t } = useTranslation();
  const last = lastContact(tokens);
  if (last !== null) {
    return <span className="badge">{t('tensoft.lastContact', { at: formatDateTime(last) })}</span>;
  }
  const anyValid = tokens.some((token) => token.revokedAt === null);
  return (
    <span className={anyValid ? 'badge badge--attention' : 'badge'}>
      {anyValid ? t('tensoft.neverContacted') : t('tensoft.notConnected')}
    </span>
  );
}

function TokenTable({
  tokens,
  freshId,
  busy,
  onRevoke,
}: {
  tokens: AgentToken[];
  freshId: string | null;
  busy: boolean;
  onRevoke: (token: AgentToken) => void;
}) {
  const { t } = useTranslation();
  const ordered = useMemo(() => orderTokens(tokens), [tokens]);

  return (
    <table className="list tensoft__list">
      <thead>
        <tr>
          <th>{t('tensoft.column.label')}</th>
          <th>{t('tensoft.column.created')}</th>
          <th>{t('tensoft.column.lastUsed')}</th>
          <th>{t('tensoft.column.state')}</th>
          <th aria-label={t('tensoft.column.actions')} />
        </tr>
      </thead>
      <tbody>
        {ordered.map((token) => {
          const revoked = token.revokedAt !== null;
          const classes = ['row'];
          if (revoked) classes.push('row--inactive');
          if (token.id === freshId) classes.push('row--fresh');
          return (
            <tr key={token.id} className={classes.join(' ')}>
              <td>{token.label}</td>
              <td className="tensoft__time">{formatDateTime(token.createdAt)}</td>
              <td className="tensoft__time">
                {token.lastUsedAt ? formatDateTime(token.lastUsedAt) : t('tensoft.never')}
              </td>
              <td>
                {revoked
                  ? t('tensoft.revokedAt', { at: formatDateTime(token.revokedAt as string) })
                  : t('tensoft.valid')}
              </td>
              <td className="row__actions">
                {revoked ? null : (
                  <button
                    type="button"
                    disabled={busy}
                    aria-label={t('tensoft.revokeFor', { label: token.label })}
                    onClick={() => onRevoke(token)}
                  >
                    {t('tensoft.revoke')}
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

type CopyState = 'idle' | 'copied' | 'manual';

function IssuedToken({ state }: { state: AgentTokensState }) {
  const { t } = useTranslation();
  const copyButton = useRef<HTMLButtonElement>(null);
  const warnId = useId();
  const tokenText = useRef<HTMLElement>(null);
  const [copy, setCopy] = useState<CopyState>('idle');
  const issued = state.issued;
  const backend = uploaderBackendUrl(window.location) ?? t('tensoft.backendPlaceholder');

  // A FÓKUSZ IDE UGRIK: a kártya alján jelenik meg, és billentyűvel a
  // könyvelő különben az űrlapon maradna, a token pedig a képernyőn kívül.
  useEffect(() => {
    copyButton.current?.focus();
  }, []);

  if (issued === null) {
    return null;
  }

  const onCopy = () => {
    // A vágólap megtagadhatja (régebbi böngésző, nem biztonságos kapcsolat).
    // Ilyenkor kijelöljük a szöveget — a Ctrl+C a könyvelő keze ügyében marad.
    if (!navigator.clipboard) {
      selectToken();
      return;
    }
    navigator.clipboard
      .writeText(issued.token)
      .then(() => setCopy('copied'))
      .catch(() => selectToken());
  };

  const selectToken = () => {
    const node = tokenText.current;
    const selection = window.getSelection();
    if (node && selection) {
      selection.selectAllChildren(node);
    }
    setCopy('manual');
  };

  return (
    <section className="tensoft__once" aria-label={t('tensoft.issuedTitle')}>
      <p id={warnId} className="tensoft__warn">
        {t('tensoft.issuedWarning', { label: issued.label })}
      </p>
      <div className="tensoft__tokenRow">
        <code ref={tokenText} className="tensoft__token">
          {issued.token}
        </code>
        {/* A fókusz ide ugrik — a figyelmeztetést a gomb leírásaként olvassa fel
            a képernyőolvasó, különben csak annyit hallana: „Másolás". */}
        <button
          ref={copyButton}
          type="button"
          className="settings__primary"
          aria-describedby={warnId}
          onClick={onCopy}
        >
          {t('tensoft.copy')}
        </button>
      </div>
      {copy === 'idle' ? null : (
        <output className="tensoft__copied">
          {copy === 'copied' ? t('tensoft.copied') : t('tensoft.copyManually')}
        </output>
      )}
      <p className="tensoft__hint">{t('tensoft.installHint')}</p>
      <code className="tensoft__cmd">
        {`powershell -ExecutionPolicy Bypass -File .\\Install-FaktFeltolto.ps1 -BackendUrl ${backend}`}
      </code>
      <div className="tensoft__done">
        <button type="button" onClick={state.dismissIssued}>
          {t('tensoft.dismiss')}
        </button>
        <span className="tensoft__hint">{t('tensoft.lostHint')}</span>
      </div>
    </section>
  );
}

function WriteError({ state }: { state: AgentTokensState }) {
  const { t } = useTranslation();
  if (state.writeError === null) {
    return null;
  }
  return (
    <output className="notice">
      {state.writeError.kind === 'invalid' ? state.writeError.message : t('tensoft.writeFailed')}
    </output>
  );
}

function IssueForm({ state }: { state: AgentTokensState }) {
  const { t } = useTranslation();
  const inputId = useId();
  const input = useRef<HTMLInputElement>(null);
  const [label, setLabel] = useState('');
  const length = label.trim().length;
  // ⚠️ AMÍG EGY TOKEN A DOBOZBAN VÁR, ÚJAT NEM LEHET KIADNI: a második válasz
  // felülírná az elsőt, és az első örökre elveszne — kimásolatlanul.
  const blocked = state.busy || state.issued !== null;
  const ready = length >= LABEL_MIN && length <= LABEL_MAX && !blocked;

  // A MEZŐ CSAK SIKER UTÁN ÜRÜL: egy 400-as válasz után a könyvelő a saját
  // szövegét javítja, nem újragépeli.
  // ÉS A DOBOZ ELTŰNÉSEKOR A FÓKUSZ IDE JÖN: a fókuszált gomb („Elmentettem")
  // megszűnik, és billentyűvel a lap tetejéről kellene visszatabolni (D9).
  const issuedId = state.issued?.id ?? null;
  const wasIssued = useRef(false);
  useEffect(() => {
    if (issuedId !== null) {
      setLabel('');
      wasIssued.current = true;
    } else if (wasIssued.current) {
      wasIssued.current = false;
      input.current?.focus();
    }
  }, [issuedId]);

  return (
    <form
      className="tensoft__new"
      onSubmit={(event) => {
        event.preventDefault();
        if (ready) {
          state.issue(label);
        }
      }}
    >
      <div className="tensoft__field">
        <label htmlFor={inputId}>{t('tensoft.newLabel')}</label>
        <input
          ref={input}
          id={inputId}
          value={label}
          maxLength={LABEL_MAX}
          placeholder={t('tensoft.newPlaceholder')}
          onChange={(event) => setLabel(event.target.value)}
        />
      </div>
      <button type="submit" disabled={!ready}>
        {t('tensoft.issue')}
      </button>
    </form>
  );
}
